import { useCallback, useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { CircularProgress, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import _ from 'lodash';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import {
    AUTO_COMPLETE_DEFAULT,
    isEmailValid,
    SORT_ASC,
    USE_FORM_DEFAULT_OPTIONS,
} from '../../../utilities/FormUtilities';
import NgpTextField from '../../form/NgpTextField';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import {
    DATASOURCE_TYPE,
    getAddButtonText,
    getDialogTableColumns,
    getDialogTableDataSource,
    getEmptyGridText,
    NOTIFY_ALL_FIELD,
    NOTIFY_EMAIL_FIELD,
    NOTIFY_NOTIFY_IDS_FIELD,
    SELECTION_MODE,
} from './NotificationPolicyConstants';
import { splitEmailString } from '../../../utilities/StringUtilities';
import ListSelectorField from '../../form/ListSelectorField';
import { NAME_FIELD } from '../service-tests/ServiceTestAdminConstants';
import { useSettingStyles } from './NotificationPolicySettingsStyles';

const formId = 'notificationFrmNotify';
const addButtonText = t`Ok`;
const updateButtonText = t`Update`;

const defaultFormValues = { [NOTIFY_ALL_FIELD]: true };

const initialSort = [{ property: NAME_FIELD, direction: SORT_ASC }];

export default function EditNotifyForm({
    selectedRecord = {},
    selectedList = [],
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
    selectedType,
    setSelectionIsDirty,
}) {
    const styles = useSettingStyles();
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: defaultFormValues,
    });
    const internalId = useRef(null);
    const buttonText = useRef(addButtonText);
    const { handleSubmit, formState, reset, getValues, watch, trigger } = methods;
    const [saving, setSaving] = useState(false);
    const [selectionMode, setSelectionMode] = useState(SELECTION_MODE.all);
    const [validSelection, setValidSelection] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const selectedIds = watch(NOTIFY_NOTIFY_IDS_FIELD);

    useEffect(() => {
        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];
            buttonText.current = selectedRecord[INTERNAL_ID_FIELD] ? updateButtonText : addButtonText;

            const mode =
                selectedRecord.all || _.isEmpty(_.get(selectedRecord, 'ids', []))
                    ? SELECTION_MODE.all
                    : SELECTION_MODE.selection;
            setSelectionMode(mode);

            const notify = {
                ...selectedRecord,
                selectionMode: mode,
            };

            reset(notify);
        }
    }, [selectedRecord, reset]);

    const isSelectionModeValid = useCallback(() => {
        if (selectionMode === SELECTION_MODE.all) {
            return true;
        }

        return !_.isEmpty(getValues(NOTIFY_NOTIFY_IDS_FIELD));
    }, [selectionMode, getValues]);

    useEffect(() => {
        setValidSelection(isSelectionModeValid());
        trigger(NOTIFY_EMAIL_FIELD);
    }, [selectedIds, isSelectionModeValid, trigger]);

    const handleCleanup = () => {
        internalId.current = null;
        buttonText.current = addButtonText;
        setSelectedRecord(undefined);
        setSubmitAttempted(false);
        reset(defaultFormValues);
    };

    const handleFormSubmit = async (dataToSave = {}) => {
        if (!isSelectionModeValid()) {
            setSubmitAttempted(true);
            return;
        }
        if (internalId) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        dataToSave.all = selectionMode === SELECTION_MODE.all;
        dataToSave.selectionMode = selectionMode;
        if (dataToSave.all) {
            dataToSave.ids = [];
        }

        setSaving(true);

        try {
            await onConfirm(dataToSave);
            handleCleanup();
        } finally {
            setSaving(false);
        }
    };

    const handleSelectionModeChange = (e) => {
        const newMode = e.target.value;
        setSelectionMode(newMode);
        setSubmitAttempted(false);
        setValidSelection(isSelectionModeValid());
    };

    const emailValid = {
        validate: (value) => emailValidations(value),
    };

    const emailValidations = (value) => {
        if (!validEmails(value))
            return i18n._(
                t`Please enter one or more valid email addresses (separated by semi-colons, commas, or spaces)`
            );
        if (!validDuplicated(value)) return i18n._(t`Email already exists for overlapping selection`);
    };

    /**
     * See if we have a duplicate - this needs to allow duplicates for disjoint entity selections, and
     * handles multiple email recipients.
     * @param emails
     * @returns {boolean}
     */
    const validDuplicated = (emails) => {
        const lowercaseEmails = emails ? new Set(splitEmailString(_.trim(emails.toLowerCase()))) : new Set();
        const all = selectionMode === SELECTION_MODE.all;
        const newIds = all ? null : new Set(getValues('ids'));
        const index = selectedList.indexOf(selectedRecord);
        for (let i = 0; i < selectedList.length; i++) {
            // Don't compare current against itself when editing
            if (i !== index) {
                const notifyRecord = selectedList[i];
                if (notifyRecord !== selectedRecord) {
                    const overlapEmails = new Set(
                        splitEmailString(_.trim(notifyRecord.email.toLowerCase())).filter((e) => lowercaseEmails.has(e))
                    );
                    if (overlapEmails.size) {
                        // There is at least one email shared between the current one and the others
                        if (all || notifyRecord.all) {
                            // If either record is selection type "All", this is a duplicate
                            return false;
                        }
                        const overlapIds = notifyRecord.ids
                            ? new Set(notifyRecord.ids.filter((id) => newIds.has(id)))
                            : null;
                        if (overlapIds && overlapIds.size) {
                            // If there is an overlap between selected ids, this is also a duplicate
                            return false;
                        }
                    }
                }
            }
        }

        return true;
    };

    const validEmails = (emails) => {
        const emailList = splitEmailString(emails);
        const allValid = emailList.every((e) => isEmailValid(_.trim(e)));
        return allValid;
    };

    const startIcon = saving && <CircularProgress color="inherit" variant="indeterminate" size={16} />;

    return (
        <NgpTwoButtonDialog
            title={
                selectedRecord?.[INTERNAL_ID_FIELD] ? <Trans>Edit Email</Trans> : <Trans>Add Email Notification</Trans>
            }
            confirmButtonText={<Trans>Ok</Trans>}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid || saving}
            confirmButtonProps={{ startIcon }}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
            maxWidth="md"
            fullWidth
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={formId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <FormSection>
                        <NgpTextField
                            fieldName={NOTIFY_EMAIL_FIELD}
                            label={<Trans>Email</Trans>}
                            required
                            fieldRules={emailValid}
                            inputProps={{
                                size: 65,
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />
                        <RadioGroup
                            value={selectionMode}
                            onChange={handleSelectionModeChange}
                            style={{ marginLeft: 4, marginTop: 16 }}
                        >
                            <FormControlLabel
                                value={SELECTION_MODE.all}
                                label={t`All ${selectedType.titlePlural}`}
                                control={<Radio size="small" style={{ padding: 2 }} />}
                                style={{ marginBottom: 4, marginTop: 4 }}
                            />
                            <FormControlLabel
                                value={SELECTION_MODE.selection}
                                label={t`Selected ${selectedType.titlePlural}`}
                                control={<Radio size="small" style={{ padding: 2 }} />}
                                style={{ marginBottom: 16, marginTop: 4 }}
                            />
                        </RadioGroup>
                        {selectionMode === SELECTION_MODE.selection && !validSelection && submitAttempted && (
                            <h3 className={styles.selectionError}>
                                One or more {selectedType.titlePlural} need to be added to this notification
                            </h3>
                        )}
                        <div
                            style={{
                                minHeight: 250,
                                display: selectionMode === SELECTION_MODE.selection ? undefined : 'none',
                            }}
                        >
                            <ListSelectorField
                                gridColumns={getDialogTableColumns(selectedType)}
                                sorters={initialSort}
                                dbSelected={selectedRecord[NOTIFY_NOTIFY_IDS_FIELD]}
                                selectedDataSource={
                                    getDialogTableDataSource(selectedType)[DATASOURCE_TYPE.selectedDataSource]
                                }
                                availableDataSource={
                                    getDialogTableDataSource(selectedType)[DATASOURCE_TYPE.availableDataSource]
                                }
                                idsDataSource={getDialogTableDataSource(selectedType)[DATASOURCE_TYPE.idsDataSource]}
                                fieldName={NOTIFY_NOTIFY_IDS_FIELD}
                                addButtonText={getAddButtonText(selectedType)}
                                setSelectionIsDirty={setSelectionIsDirty}
                                isMultiRemove={false}
                                selectedEmptyGridText={getEmptyGridText(selectedType)}
                            />
                        </div>
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
